// Scansione guidata (§6.16): edge-detection + auto-crop lato client con
// jscanify (OpenCV.js). Le librerie sono pesanti (~8MB) → caricate ON-DEMAND
// da CDN solo quando si apre lo scanner. Nessun OCR (vedi README §1).

const OPENCV_URL = 'https://docs.opencv.org/4.10.0/opencv.js';
const JSCANIFY_URL = 'https://cdn.jsdelivr.net/npm/jscanify@1.3.0/src/jscanify.min.js';

/** Minimo dell'API jscanify usata qui. */
export interface Scanner {
  highlightPaper(canvas: HTMLCanvasElement, opts?: { color?: string; thickness?: number }): HTMLCanvasElement;
  extractPaper(canvas: HTMLCanvasElement, width: number, height: number): HTMLCanvasElement;
}

declare global {
  interface Window {
    cv?: unknown & { onRuntimeInitialized?: () => void; Mat?: unknown };
    jscanify?: new () => Scanner;
  }
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      resolve();
      return;
    }
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Impossibile caricare ${src}`));
    document.head.appendChild(s);
  });
}

function waitOpenCVReady(): Promise<void> {
  return new Promise((resolve, reject) => {
    const cv = window.cv;
    if (cv && (cv as { Mat?: unknown }).Mat) return resolve();
    const timeout = setTimeout(() => reject(new Error('Timeout inizializzazione OpenCV')), 30000);
    const tick = () => {
      const c = window.cv as { Mat?: unknown } | undefined;
      if (c && c.Mat) {
        clearTimeout(timeout);
        resolve();
      } else {
        setTimeout(tick, 150);
      }
    };
    tick();
  });
}

let scannerPromise: Promise<Scanner> | null = null;

/** Carica (una sola volta) OpenCV + jscanify e restituisce uno Scanner pronto. */
export function loadScanner(): Promise<Scanner> {
  if (!scannerPromise) {
    scannerPromise = (async () => {
      await loadScript(OPENCV_URL);
      await waitOpenCVReady();
      await loadScript(JSCANIFY_URL);
      if (!window.jscanify) throw new Error('jscanify non disponibile');
      return new window.jscanify();
    })().catch((e) => {
      scannerPromise = null; // consente un nuovo tentativo
      throw e;
    });
  }
  return scannerPromise;
}

// --- Handoff immagine acquisita → Form inserimento (§6.6) -------------------
// Buffer transitorio in memoria (dataURL) per passare lo scatto a NuovoScreen
// senza serializzare grandi blob in URL/localStorage.
let pending: { dataUrl: string; filename: string } | null = null;

export function setPendingScan(dataUrl: string, filename = 'scansione.jpg'): void {
  pending = { dataUrl, filename };
}

export function takePendingScan(): { dataUrl: string; filename: string } | null {
  const p = pending;
  pending = null;
  return p;
}

/** Converte un dataURL in File per l'upload (api.uploadFile). */
export function dataUrlToFile(dataUrl: string, filename: string): File {
  const [head, b64] = dataUrl.split(',');
  const mime = /data:([^;]+)/.exec(head)?.[1] ?? 'image/jpeg';
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new File([bytes], filename, { type: mime });
}
